export type FindReplacePair = { find: string; replace: string }
export type ParsedSubstitutions = { pairs: FindReplacePair[] } | { error: string }

export function parseMatrixSubstitutions(value: string): ParsedSubstitutions | null {
  const lines = value.split(/\r\n|\n|\r/).filter((line) => line.trim())
  const substitutions = lines.filter((line) => line.startsWith('%s/')).length
  if (!substitutions) {
    return null
  }
  if (substitutions !== lines.length) {
    return { error: 'Do not mix substitution lines and plain text.' }
  }
  return parseSubstitutions(value)
}

function regexFind(find: string): { pattern: string; flags: string } | null {
  const match = find.match(/^\/(.+)\/([gimsuvy]*)$/)
  if (!match?.[1]) {
    return null
  }
  const flags = match[2] ?? ''
  try {
    void new RegExp(match[1], flags)
    return { pattern: match[1], flags }
  } catch {
    return null
  }
}

function escapePart(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\//g, '\\/')
}

export function serializeSubstitutions(pairs: readonly FindReplacePair[]): string {
  return pairs
    .map(({ find, replace }) => {
      const regex = regexFind(find)
      return regex
        ? `%s/${escapePart(regex.pattern)}/${escapePart(replace)}/${regex.flags}`
        : `%s/${escapePart(find)}/${escapePart(replace)}`
    })
    .join('\n')
}

export function parseSubstitutions(value: string): ParsedSubstitutions {
  const pairs: FindReplacePair[] = []
  const lines = value.split(/\r\n|\n|\r/)
  for (const [index, line] of lines.entries()) {
    if (!line.trim()) {
      continue
    }
    const error = (reason: string): ParsedSubstitutions => ({ error: `Line ${index + 1}: ${reason}` })
    if (!line.startsWith('%s/')) {
      return error('Start each substitution with %s/.')
    }
    const parts: string[] = ['']
    for (let position = 3; position < line.length; position++) {
      const character = line.charAt(position)
      if (character === '\\') {
        const escaped = line.charAt(++position)
        if (escaped !== '/' && escaped !== '\\') {
          return error('Escape a slash as \\/ and a backslash as \\\\.')
        }
        parts[parts.length - 1] += escaped
      } else if (character === '/') {
        parts.push('')
      } else {
        parts[parts.length - 1] += character
      }
    }
    if (parts.length < 2) {
      return error('Separate the find and replacement with /.')
    }
    if (parts.length > 3) {
      return error('Escape literal slashes as \\/; only regex substitutions have a trailing /flags.')
    }
    const find = parts[0] ?? ''
    const replace = parts[1] ?? ''
    if (!find) {
      return error('The find value cannot be empty.')
    }
    if (parts.length === 3) {
      const flags = parts[2] ?? ''
      if (!/^[gimsuvy]*$/.test(flags)) {
        return error('Unknown regex flags. Use only g, i, m, s, u, v or y.')
      }
      try {
        void new RegExp(find, flags)
      } catch {
        return error('The regular expression or its flags are invalid.')
      }
      pairs.push({ find: `/${find}/${flags}`, replace })
    } else {
      pairs.push({ find, replace })
    }
  }
  return { pairs }
}
